import { Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Banner } from "components/common/Banner";
import {
    CustomUpgrade,
    useCustomUpgrades
} from "in_queue/contexts/CustomUpgradesContext";
import {
    ValidationState,
    VALIDATION_RISK_REASON
} from "in_queue/utils/customUpgradeValidation";

export const RISK_WARNING_TITLE = "Using a non-validated upgrade";

export type RiskState = Exclude<ValidationState, "usable">;

// Null when the upgrade is safe to apply.
export const getRiskState = (upgrade: CustomUpgrade): RiskState | null => {
    const { state } = upgrade.modelSetValidationInfo.status;
    return state === "usable" ? null : state;
};

export const riskMessage = (upgrade: CustomUpgrade, state: RiskState): string =>
    `"${upgrade.name}" ${VALIDATION_RISK_REASON[state]}`;

interface CustomUpgradeRiskBannerProps {
    readonly selectedUpgradeId: string | null;
    readonly className?: string;
}

/** The standing warning for a selected upgrade that isn't validated; null otherwise. */
export const CustomUpgradeRiskBanner: React.FC<
    CustomUpgradeRiskBannerProps
> = ({ selectedUpgradeId, className }) => {
    const customUpgrades = useCustomUpgrades();

    if (selectedUpgradeId === null || customUpgrades === "loading") {
        return null;
    }

    const upgrade = customUpgrades.find(
        (candidate) => candidate.id === selectedUpgradeId
    );
    if (upgrade === undefined) {
        return null;
    }

    const risk = getRiskState(upgrade);
    if (risk === null) {
        return null;
    }

    return (
        <Banner
            className={className}
            intent={Intent.WARNING}
            icon={IconNames.WARNING_SIGN}
            title={RISK_WARNING_TITLE}
            description={riskMessage(upgrade, risk)}
        />
    );
};
